import { useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Chip, ContentLoader, Heading } from '@frontend/design-system';
import { LocationAccordionList } from '../../../../../components/location-accordion-list';
import { MAX_CALENDAR_LOCATION_COUNT } from '../../../../../constants';
import { useScheduleActionsContainerMethod } from '../../../../../context/ScheduleActionsContainerContext';
import {
  useGetCalendarV3QueriesData,
  useGetFilteredCalendarEventsV3,
  useLocationTimezone,
  useSchedulingLocationInfo,
} from '../../../../../hooks';
import { CalendarHeader } from '../../../../CalendarV3/components/header';
import { convertV3CalendarEventsToV2Appointments } from '../../../../CalendarV3/helpers';
import { PatientListRow } from '../../../components/PatientListRow';
import { ScheduleMassMessageTrackingIds } from '../ScheduleMassMessages/trackingIds';

export const ScheduleMassMessagesV3 = () => {
  const { getScopeName } = useSchedulingLocationInfo();
  const {
    calendarLocationIds,
    filteredCalendarEventsData,
    calendarCounts,
    calendarTodayDate,
    isLoadingCalendarEvents,
  } = useGetCalendarV3QueriesData();

  const locationInfoList = useMemo(
    () =>
      calendarLocationIds.map((locationId) => ({
        locationId,
        name: getScopeName(locationId),
        count: filteredCalendarEventsData.filter((appt) => appt.locationId === locationId).length,
      })),
    [calendarLocationIds, getScopeName, filteredCalendarEventsData]
  );

  return (
    <div css={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div css={{ zIndex: 1, boxShadow: theme.shadows.light }}>
        <CalendarHeader
          calendarTodayDate={calendarTodayDate}
          calendarEventsCount={calendarCounts.total}
          unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
          insuranceAlertCalendarEventsCount={calendarCounts.insurance}
        />
      </div>
      <div css={{ position: 'relative', flex: 1, overflowY: 'auto' }}>
        <ContentLoader
          size='medium'
          // TODO: identify why its setting this loader value tot true even if we were fetching things location wise
          // so that we can remove this max location check logic from here in loader
          show={isLoadingCalendarEvents && calendarLocationIds.length <= MAX_CALENDAR_LOCATION_COUNT}
        />
        <LocationAccordionList
          ContentComponent={SingleLocationView}
          locationInfoList={locationInfoList}
          style={css({ padding: theme.spacing(2) })}
        />
      </div>
    </div>
  );
};

interface SingleLocationViewProps {
  locationId: string;
  height?: number | string;
}

const SingleLocationView = ({ locationId, height }: SingleLocationViewProps) => {
  const { t } = useTranslation('schedule-pulse');
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const { getLocationTimezone } = useLocationTimezone();
  const { setHideTabs, setSelectedLocationId } = useScheduleActionsContainerMethod();
  const { isMultiLocation } = useSchedulingLocationInfo();
  const { filteredCalendarEventsData, calendarLocationIds } = useGetCalendarV3QueriesData();

  const isMoreThanMaxLocation = calendarLocationIds.length > MAX_CALENDAR_LOCATION_COUNT;
  const { filteredCalendarEventsData: filteredCalendarEventsByLocation, isLoadingCalendarEvents } =
    useGetFilteredCalendarEventsV3(isMoreThanMaxLocation ? [locationId] : []);

  const appointments = useMemo(() => {
    const filteredEvents = isMoreThanMaxLocation
      ? filteredCalendarEventsByLocation
      : filteredCalendarEventsData.filter((event) => event.locationId === locationId);
    const list = convertV3CalendarEventsToV2Appointments(filteredEvents, locationTimezoneMap);
    return list;
  }, [
    isMoreThanMaxLocation,
    filteredCalendarEventsData,
    filteredCalendarEventsByLocation,
    locationTimezoneMap,
    locationId,
  ]);

  const timeZone = getLocationTimezone(locationId);
  const heading = () => {
    return (
      <Heading level={3} css={{ fontSize: theme.font.size.large }}>
        {appointments.length ? t('{{count}} Patients', { count: appointments.length }) : t('No Appointments')}
      </Heading>
    );
  };

  const headingContainer = () => {
    if (isMultiLocation) {
      return <>{heading()}</>;
    } else {
      return (
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          {heading()}
          {appointments.length > 0 && <Chip.TimeZone variant='neutral'>{timeZone}</Chip.TimeZone>}
        </div>
      );
    }
  };

  return (
    <>
      <ContentLoader show={isLoadingCalendarEvents} size='medium' />
      <div css={locationTopSectionStyle}>
        {headingContainer()}
        <Button
          variant='tertiary'
          trackingId={ScheduleMassMessageTrackingIds.contactPatientsBtn}
          disabled={!appointments.length}
          onClick={() => {
            setSelectedLocationId(locationId);
            setHideTabs(true);
          }}
        >
          {t('Contact Patients')}
        </Button>
      </div>

      <div css={{ background: theme.colors.white, flex: 1 }}>
        <PaginatedList
          emptyStateConfig={{
            header: t('No Appointments found'),
            type: 'schedule',
          }}
          height={typeof height === 'number' ? height - 65 : height ?? '100%'} // 65px is the height of the top section
          listData={appointments}
          renderListItem={({ listItem }) => (
            <>
              <PatientListRow
                appointments={appointments}
                appt={listItem}
                shouldShowAppointmentModal
                locationId={locationId}
                shouldShowActionMenu
              />
              <hr css={{ border: 'none', borderBottom: `1px solid ${theme.colors.neutral20}` }} />
            </>
          )}
          endOfListText=''
        />
      </div>
    </>
  );
};

const locationTopSectionStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.colors.white,
  borderBottom: `1px solid ${theme.colors.neutral20}`,
});
